import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const BranchDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [branch, setBranch] = useState(null);
  const [location, setLocation] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBranchData = async () => {
      try {
        setLoading(true);
        
        // Cargar datos de la sucursal
        const branchData = await apiService.getBranch(id);
        setBranch(branchData);
        
        // Cargar la información de ubicación
        if (branchData.location_ubigeo) {
          const locationData = await apiService.getLocationUbigeo(branchData.location_ubigeo);
          setLocation(locationData);
        }
        
        // Cargar balances de la sucursal
        try {
          const balanceData = await apiService.getBranchBalances(id);
          setBalances(balanceData);
        } catch (balanceErr) {
          console.error('Error al cargar balances:', balanceErr);
          // No interrumpimos el flujo si falla la carga de balances
        }
      } catch (err) {
        console.error('Error al cargar datos de la sucursal:', err);
        setError('Error al cargar los datos de la sucursal. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchBranchData();
  }, [id]);

  const handleBack = () => {
    navigate('/branches');
  };

  const handleEdit = () => {
    navigate(`/branches/edit/${id}`);
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles de la Sucursal</Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!branch) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles de la Sucursal</Typography>
        </Box>
        <Alert severity="warning">No se encontró la sucursal solicitada.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles de la Sucursal</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Editar
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
              <Typography variant="h5">
                {branch.branch_name}
              </Typography>
              <Chip
                label={branch.is_enabled ? 'Activa' : 'Inactiva'}
                color={branch.is_enabled ? 'success' : 'error'}
                size="small"
                sx={{ ml: 2 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />

            {location && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {`${location.department_name}, ${location.province_name}, ${location.district_name}`}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Creado el
                </Typography>
                <Typography variant="body2">
                  {new Date(branch.created_at).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Última actualización
                </Typography>
                <Typography variant="body2">
                  {new Date(branch.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="Balances" 
              avatar={<AccountBalanceIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              {balances.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No hay información de balances disponible.
                </Typography>
              ) : (
                <List>
                  {balances.map((balance) => (
                    <ListItem key={balance.id} divider>
                      <ListItemText
                        primary={`${balance.currency_type}`}
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              Físico: {formatCurrency(balance.physical_amount, balance.currency_type)}
                            </Typography>
                            <br />
                            <Typography variant="body2" component="span">
                              Lógico: {formatCurrency(balance.logical_amount, balance.currency_type)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BranchDetail; 